import { KeyRound, LogOut } from "lucide-react";
import { Button } from "@ec2/ui";
import { useT } from "@ec2/i18n/react";
import { Sheet } from "./Sheet";

/**
 * Leave-event confirmation (profile danger zone): spells out that leaving
 * permanently deletes ALL progress, and disambiguates from the passkey flow —
 * "continue later / on another phone" wants a passkey, not a leave.
 */
export function LeaveEventSheet({
  busy,
  onConfirm,
  onCancel,
  onPasskeyInstead,
  offerPasskey = true,
}: {
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  onPasskeyInstead: () => void;
  /** Hide the "save a passkey instead" way out when the player already has one. */
  offerPasskey?: boolean;
}) {
  const t = useT();
  return (
    <Sheet label={t("ec2.profile.leave.title")} onClose={onCancel}>
      <div className="flex flex-col gap-3" data-testid="ec-leave-sheet">
        <span className="flex size-10 items-center justify-center rounded-(--radius-chip) bg-danger/15 text-danger">
          <LogOut size={20} aria-hidden />
        </span>
        <h2 className="font-display text-[21px] font-[650] text-fg">
          {t("ec2.profile.leave.title")}
        </h2>
        <p className="text-base font-medium text-fg">{t("ec2.profile.leave.body")}</p>
        {offerPasskey && (
          <>
            <p className="rounded-(--radius-chip) bg-primary/10 px-3 py-2.5 text-[13px] font-medium text-fg-secondary">
              {t("ec2.profile.leave.passkeyHint")}
            </p>
            <button
              type="button"
              onClick={onPasskeyInstead}
              data-testid="ec-leave-passkey-instead"
              className="inline-flex items-center gap-2 self-start text-base font-semibold text-primary focus-visible:outline-2 focus-visible:outline-primary"
            >
              <KeyRound size={18} aria-hidden />
              {t("ec2.profile.leave.passkeyInstead")}
            </button>
          </>
        )}
        <div className="mt-1 flex flex-col gap-2">
          <Button
            variant="primary"
            className="w-full bg-danger"
            disabled={busy}
            onClick={onConfirm}
            data-testid="ec-leave-confirm"
          >
            {t("ec2.profile.leave.confirm")}
          </Button>
          <Button
            variant="ghost"
            className="w-full"
            onClick={onCancel}
            data-testid="ec-leave-cancel"
          >
            {t("ec2.common.cancel")}
          </Button>
        </div>
      </div>
    </Sheet>
  );
}
